<template>
  <div class="card card-box direct-messages">
    <!-- Header -->
    <div class="card-header header__direct-messages">
      <div class="d-flex align-items-center justify-content-between shadow-none w-100">
        <div class="px-2">
          <div class="text-dark">Direct Message</div>

          <div class="h6 font-weight-bold mt-2">
            Talking to
            <span>{{ mode === 'learner' ? 'Teachers' : learner.username }}</span>
          </div>
        </div>

        <div class="px-2">
          <slot name="header-right" />
        </div>
      </div>
    </div>

    <!-- Hide or view previous response -->
    <!-- <b-list-group-item
      v-if="messages && messages.length - alwaysDisplay > 0"
      class="
        d-flex
        flex-row
        align-items-center
        text-primary
        rounded-0
        border-left-0 border-right-0
        py-3
      "
      :class="`${
        isHideResponse
          ? 'btn__view-previous-response'
          : 'btn__hide-previous-response'
      }`"
      style="cursor: pointer"
      @click="isHideResponse = !isHideResponse"
    >
      <font-awesome-icon icon="comments" class="mr-2 ml-2" />

      {{ `${isHideResponse ? "View" : "Hide"} previous responses` }}
    </b-list-group-item> -->

    <!-- Messages list -->
    <b-col cols="12" class="container__direct-messages px-0">
      <div class="d-flex justify-content-center">
        <b-spinner
          v-show="isLoadingMoreMessages"
          class="my-2"
          label="Loading..."
          variant="dark"
          style="width: 2rem; height: 2rem"
        />

        <a
          v-show="!isLoadingMoreMessages && channel && channel.hasMoreMessages"
          href="javascript:void(0)"
          class="mt-3"
          @click="loadMoreMessages"
        >
          <small>
            <b-button pill variant="outline-primary" size="sm">Load more</b-button>
          </small>
        </a>
      </div>

      <b-list-group-item
        v-for="(message, index) in messages"
        :key="`message-${message.messageId}`"
        :ref="`message-${message.messageId}`"
        class="rounded-0 py-3 border-left-0 border-right-0"
        :class="{ 'border-top-0': index === 0 }"
      >
        <BodyMessage
          :channel-url="channelUrl"
          :message="message"
          :user="user"
          @emitDeleteMessage="emitDeleteMessage"
          @onActionMessageSuccess="onActionMessageSuccess"
        />
      </b-list-group-item>
    </b-col>

    <!-- Message footer reseponse -->
    <div class="mb-4">
      <ResponseBlock
        :channel-url="channelUrl"
        :user="user"
        @onActionMessageSuccess="onActionMessageSuccess"
      />
    </div>
  </div>
</template>

<script>
import { Channel, Learner, User, Message } from '@/models';
import ResponseBlock from './ResponseBlock';
import BodyMessage from './BodyMessage';

export default {
  name: 'DirectMessage',

  components: { ResponseBlock, BodyMessage },

  props: {
    alwaysDisplay: {
      type: Number,
      default: 1
    },

    channelUrl: {
      type: String,
      default: ''
    },

    user: {
      type: User,
      default: null
    },

    learner: {
      type: [Learner, User],
      default: null
    },

    mode: {
      type: String,
      default: 'learner' // "learner" or "teacher"
    },

    messagesPerPage: {
      type: Number,
      default: 10
    }
  },

  data() {
    return {
      isHideResponse: true,
      isLoadingMessages: false,
      isLoadingMoreMessages: false,
      isUpdatedOnQueryChanged: false
    };
  },

  computed: {
    channel() {
      return Channel.findChannel(this.channelUrl);
    },

    hasMoreMessages() {
      return this.channel.hasMoreMessages;
    },

    messages() {
      return Message.inChannel(this.channel.url);
    }
  },

  watch: {
    channel: {
      immediate: true,
      handler: 'loadMessages'
    }
  },

  async mounted() {
    console.log('DirectMessage mounted!!!');

    try {
      const isLearnerJoined = await Channel.isLearnerJoined(this.channel.url, this.learner.id);

      if (!isLearnerJoined) {
        console.log('Learner is not a member was detected');

        if (this.mode === 'teacher') {
          await Channel.api().inviteLearner(this.channel.url, this.learner.id);

          this.$sentry.captureMessage(
            `[Resolved by teacher] Is not a member learner:${this.learner.id}, channel:${this.channel.url}`
          );
        } else {
          await Channel.api().joinAsLearner(this.channel.url, this.learner.id);

          this.$sentry.captureMessage(
            `[Resolved by learner] Is not a member learner:${this.learner.id}, channel:${this.channel.url}`
          );
        }
      }
    } catch (error) {
      this.$sentry.captureException(error);
    }

    try {
      Channel.createEventHandlers(`directMessageListener`, {
        onMessageReceived: async (channel) => {
          if (channel && channel.url && this.channel.url === channel.url) {
            console.log('onMessageReceived!!!');
            await this.channel.messagesAsRead(this.user.id);
          }
        }
      });

      await this.channel.messagesAsRead(this.user.id);
    } catch (error) {
      this.$sentry.captureException(error);
    }
  },

  beforeDestroy() {
    Channel.removeEventHandler(`directMessageListener`);
  },

  methods: {
    async loadMoreMessages() {
      let messsages;

      this.isLoadingMoreMessages = true;

      try {
        messsages = await this.loadMessages(this.channel);
      } catch (error) {
        this.$toast.error('Cannot load more messages, please try again');
      } finally {
        this.isLoadingMoreMessages = false;
      }

      return messsages;
    },

    async loadMessages(channel) {
      let messages;

      if (channel) {
        this.isLoadingMessages = true;

        try {
          messages = await channel.loadMessages({
            limit: this.messagesPerPage
          });

          if (messages && messages.length) {
            const firstMessage = messages[0];

            this.scrollToMessage(firstMessage);
          }
        } catch (error) {
          console.error(error);
          this.$toast.error('Cannot load messages, please try again');
        } finally {
          this.isLoadingMessages = false;
        }
      }

      return messages;
    },

    scrollToMessage(message) {
      this.$nextTick(() => {
        setTimeout(() => {
          const refMessage = this.$refs[`message-${message.messageId}`];

          if (refMessage && refMessage.length) {
            refMessage[0].scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      });
    },

    emitDeleteMessage(messageId) {
      this.$emit('emitDeleteMessage', messageId);
    },

    onActionMessageSuccess() {
      this.$emit('onActionMessageSuccess');
    }
  }
};
</script>

<style lang="scss" scoped>
.response__date {
  color: rgba(7, 9, 25, 0.5);
}

.response__content {
  margin-left: 56px;
}

.header__direct-messages {
  width: 100%;
  background-color: rgb(248, 249, 255);
}

.direct-message-block {
  border-bottom: 2px solid #eeeff8;
}

.container__direct-messages {
  max-height: 50vh;
  overflow: auto;
}
</style>
